import { ManifestQueryAPI, ManifestResolverAPI } from '../../handlers';
import {
  DependenciesContributor,
  DependencyToken,
  IDependencyAPI,
} from '../../dependencies';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { IManifestChangeDetection } from '../../ManifestChangeDetection';
import { ManifestStore } from '../../stores';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export class DependenciesProvider {
  readonly #manifestResolverAPI: ManifestResolverAPI;
  readonly #manifestQueryAPI: ManifestQueryAPI;

  constructor(
    private readonly dependenciesContributor: DependenciesContributor,
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly manifestStore: ManifestStore,
    private readonly manifestChangeDetection: IManifestChangeDetection,
    private readonly manifestServiceFlags: ManifestServiceFlags,
    private readonly manifestServiceStats: ManifestServiceStats,
  ) {
    this.#manifestResolverAPI = new ManifestResolverAPI(
      this.dependenciesContributor,
      this.editorPointerAPI,
      this.manifestStore,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );

    this.#manifestQueryAPI = new ManifestQueryAPI(
      this.dependenciesContributor,
      this.editorPointerAPI,
      this.manifestStore,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );
  }

  withOverrides(
    tokens:
      | Partial<{
          [K in DependencyToken]: Extract<IDependencyAPI, { type: K }>['api'];
        }>
      | undefined,
  ) {
    const withOverrides = tokens && Object.keys(tokens ?? {}).length;

    if (!withOverrides) {
      return this;
    }

    const dependenciesContributor = new DependenciesContributor();

    Object.entries(this.dependenciesContributor.getTokens()).forEach(
      ([token, impl]) => {
        dependenciesContributor.set(token as DependencyToken, impl as any);
      },
    );

    Object.entries(tokens).forEach(([token, impl]) => {
      if (impl) {
        dependenciesContributor.set(token as DependencyToken, impl as any);
      }
    });

    return new DependenciesProvider(
      dependenciesContributor,
      this.editorPointerAPI,
      this.manifestStore,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );
  }

  get<TToken extends DependencyToken>(token: TToken) {
    return this.dependenciesContributor.get(token);
  }

  getManifestQueryAPI() {
    return this.#manifestQueryAPI;
  }

  getManifestResolverAPI() {
    return this.#manifestResolverAPI;
  }
}
